/*
 * Encodes a HTML string.
 *
 *      e.g.: encodeHtml("<em>Hi!</em>")
 *      -> "&lt;em&gt;Hi!&lt;/em&gt;"
 *
 * @param  {string} value       Input value
 * @return {string}             Encoded output
 */
export const encodeHtml = (value: string | number): any => {
	if (typeof value !== 'string') {
		return value;
	}

	return value.replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

/*
 * Uppercases first character of a string
 * @param {string} value
 * @return {string}
 */
export const ucfirst = (value: string): string => {
	return `${value.charAt(0).toUpperCase()}${value.substr(1)}`;
};
/*
 * Removes the trailing slash if exists
 * @param {string} url
 * @return {string}
 */
export const removeTrailingSlash = (url: string): string => (url[0] === '/' ? url.substr(1, url.length) : url);

/*
 * Renders a string template compiled with a data object.
 *
 *      e.g.: template("Hallo {forename} aus {city}!", {
 *                  forename: "Maximilian",
 *                  city: "St. Gallen"
 *            });
 *
 * @type {Function}
 * @param {string} value
 * @param {Object} data
 */
export const template = (value: string, data: Object, preventHtmlEncoding: boolean = false): string => {
	if (typeof value !== 'string') {
		return '';
	} else if (typeof data !== 'object') {
		return value;
	}

	for (const prop in data) {
		if (data.hasOwnProperty(prop)) {
			const expr = new RegExp(`{${prop}}`, 'gm');

			if (!preventHtmlEncoding) {
				if (typeof data[prop] === 'string') {
					data[prop] = encodeHtml(data[prop]);
				}
			}

			value = value.replace(expr, data[prop]);
		}
	}

	return value;
};

/*
 * Cleans a template with placeholders while replacing with {replaceValue}
 *
 *      e.g.: cleanTemplate("Hello {value}!") => "Hello !"
 *
 * @param {string} template
 * @param {string?=""} replaceValue
 * @return {string}
 */
export const cleanTemplate = (tpl: string, replaceValue: string = ''): string => {
	const matchAllPlaceholdersExpr = /\{([^{}]+)\}/g;
	return tpl.replace(matchAllPlaceholdersExpr, replaceValue);
};

/*
 * Compiles a dynamic route with a dataset.
 *
 *      e.g.: compileDynamicRoute("/program/:id/:type", { id: 10, type: "detail" });
 *      -> "/program/10/detail"
 *
 * @param  {string} route       Route template
 * @param  {Object} data        Dataset
 * @return {string}             Compiled route
 */
export const compileDynamicRoute = (route: string, data: { [key: string]: string | number } = {}): string => {
	if (typeof route !== 'string') {
		return '';
	} else if (typeof data !== 'object') {
		return route;
	}

	for (const prop in data) {
		if (data.hasOwnProperty(prop)) {
			const expr = new RegExp(`:${prop}`, 'gm');

			if (typeof data[prop] === 'string' || typeof data[prop] === 'number') {
				data[prop] = encodeHtml(`${data[prop]}`);
				route = route.replace(expr, data[prop].toString());
			}
		}
	}

	return route;
};

/*
 * Converts a camelcase string to a dashed string for
 * e.g. CSS class generation etc.
 * @param  {string} str         Camel case input
 * @return {string}             Dashed output
 */
export const toDashed = (str: string) => {
	if (!str || typeof str !== 'string') {
		return '';
	}

	return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

export const toNumericalString = (val: string = ''): string => {
	return val.replace(',', '.');
};

export const toString = (val: any): string => {
	if (typeof val === 'string') {
		return val;
	} else if (typeof val === 'number') {
		return `${val}`;
	} else if (val.toString) {
		return val.toString();
	}

	return JSON.stringify(val);
};
